<template>
  <div v-loading="loading" class="app-container">
    <el-form-item :label="$t('common.domain')">
      <el-select
        v-model="selectedDomainID"
        class="search-select"
        type="select"
        style="width: 20%; padding-bottom: 10px"
        :placeholder="$t('common.domain')"
        @input="handleDomainChange($event)"
      >
        <el-option v-for="domain in domains" :key="domain.id" :label="domain.name" :value="domain.id" />
      </el-select>
      <editor-button :disabled="!selectedDomainID" action="save" style="float:right;" @click="saveData()" />
      <editor-button
        :disabled="!selectedDomainID"
        button-translation="planning.exportSelection"
        action="primary"
        :icon-only="false"
        style="float:right;"
        @click="exportSelection()"
      />
      <editor-button
        :disabled="!selectedDomainID"
        button-translation="planning.ordersToPlan"
        action="primary"
        :icon-only="false"
        style="float:right;"
        @click="getOrdersToPlan()"
      />
      <editor-button
        :disabled="!selectedDomainID || selectedWeek == currentWeek"
        button-translation="planning.makeCurrent"
        action="primary"
        :icon-only="false"
        style="float:right;"
        @click="makeCurrent()"
      />
    </el-form-item>
    <el-form-item :label="$t('planning.weekNumber')">
      <date-picker
        v-model="selectedWeek"
        type="week"
        :disabled="!selectedDomainID"
        :clearable="false"
        @input="handleWeekChange($event)"
      >
      </date-picker>
    </el-form-item>
    <div class="production-planning-capacity-list">
      <el-form-item :label="$t('planning.panels')">
        <el-table ref="planningList" :data="panelItems" border fit highlight-current-row>
          <el-table-column :label="$t('planning.workshopOrder')" width="50px" property="workshopOrder">
            <template slot-scope="scope">
              <span>{{ scope.row.workshopOrder }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.workshopID')" width="250px" property="workshopID">
            <template slot-scope="{ row }">
              <el-select
                v-model="row.workshopID"
                :disabled="currentPlanning.panelsPreselectedYN || panelsPlannedYN"
                size="mini"
                type="select"
                clearable
                @input="handleInputWorkshop(row, $event)"
              >
                <el-option-group :label="$t('planning.workshopID')">
                  <el-option
                    v-for="workshop in getSelectableWorkshops(row.workshopID, panelItems)"
                    :key="workshop.id"
                    :label="workshop.name"
                    :value="workshop.id"
                  />
                </el-option-group>
              </el-select>
              <div v-if="row.workshopEmpty" class="validation-errors">{{ $t('planning.workshopRequired') }}</div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.capacity')" width="200px" property="capacity">
            <template slot-scope="{ row }">
              <MDinput
                v-model="row.capacity"
                type="number"
                :readonly="readonly"
                :precision="0"
                :disabled="currentPlanning.panelsPreselectedYN || panelsPlannedYN"
                @input="handleInputCapacity(row, $event)"
              />
              <div v-if="row.capacityEmpty" class="validation-errors">{{ $t('planning.capacityRequired') }}</div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.estimatedDeliveryDate')" width="200px" property="estimatedDeliveryDate">
            <template slot-scope="{ row }">
              <date-picker v-model="row.estimatedDeliveryDate" type="date" />
            </template>
          </el-table-column>
          <el-table-column>
            <template slot-scope="{ row }">
              <span v-if="row.ignoreExcludedFabricsYN" style="color:red">
                {{ $t('planning.excludedFabricsWillBeIgnored') }}
              </span>
            </template>
          </el-table-column>
        </el-table>
        <div
          v-if="currentPlanning && currentPlanning.panelsPreselectedYN && !panelsPlannedYN"
          class="validation-errors"
        >
          {{ $t('planning.preselectionMade') }}
        </div>
        <div v-if="panelsPlannedYN" class="validation-errors">
          {{ $t('planning.alreadyPlanned') }}
          <br />
          <editor-button
            :icon-only="false"
            :disabled="!selectedDomainID || selectedWeek != currentWeek"
            :button-string="$t('planning.planAgain')"
            action="secondary"
            style="margin-top:5px;"
            @click="planAgain(panelOption)"
          />
        </div>
        <div v-show="!panelsPlannedYN">
          <div style="padding-top:5px">
            <el-checkbox
              v-model="panelsConsiderStoreAttributes"
              :disabled="
                !currentPlanning ||
                  currentPlanning.panelsPreselectedYN ||
                  panelsPlannedYN ||
                  selectedWeek != currentWeek
              "
              style="padding-left: 20px"
            >
              {{ $t('planning.considerStoreAttributes') }}
            </el-checkbox>
            <el-checkbox
              v-model="samplesOnlyYN[panelOption]"
              :disabled="
                !currentPlanning ||
                  currentPlanning.panelsPreselectedYN ||
                  panelsPlannedYN ||
                  selectedWeek != currentWeek
              "
              style="padding-left: 20px"
            >
              {{ $t('planning.samples') }}
            </el-checkbox>
            <span style="padding-left:20px">
              <label class="material-label">
                {{ $t('planning.excludedStores') }}
              </label>
              <el-select
                v-model="excludedStoreIDs[panelOption]"
                :multiple="true"
                :placeholder="$t('planning.excludedStores')"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.panelsPreselectedYN ||
                    panelsPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%; margin-bottom: 10px"
              >
                <el-option
                  v-for="store in stores"
                  :key="store.id"
                  :label="store.code + ', ' + store.name"
                  :value="store.id"
                />
              </el-select>
            </span>
            <span style="margin-left:20px">
              <label class="material-label">
                {{ $t('planning.excludedStoreAttributes') }}
              </label>
              <el-select
                v-model="excludedStoreAttributeIDs[panelOption]"
                :multiple="true"
                :placeholder="$t('planning.excludedStoreAttributes')"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.panelsPreselectedYN ||
                    panelsPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%"
              >
                <el-option
                  v-for="storeAttribute in storeAttributes"
                  :key="storeAttribute.id"
                  :label="storeAttribute.name"
                  :value="storeAttribute.id"
                />
              </el-select>
            </span>
            <span style="padding-left:20px">
              <label class="material-label">
                {{ $t('planning.installationService') }}
              </label>
              <el-select
                v-model="installationServiceOptions[panelOption]"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.panelsPreselectedYN ||
                    panelsPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%; padding-bottom: 10px"
              >
                <el-option
                  v-for="store in planInstallationServiceOptions"
                  :key="store.code"
                  :label="store.description"
                  :value="store.code"
                />
              </el-select>
            </span>
          </div>
          <div>
            <editor-button
              :icon-only="false"
              :disabled="panelsPlannedYN || !selectedDomainID || selectedWeek != currentWeek"
              :button-string="$t('planning.plan')"
              action="primary"
              style="float:left"
              @click="startSelectionWizard(panelOption)"
            />
            <editor-button
              :icon-only="false"
              :disabled="panelsPlannedYN || !selectedDomainID || selectedWeek != currentWeek"
              button-translation="planning.reset"
              action="secondary"
              class="reset-button"
              style="float:left;margin-top:0px;"
              @click="resetPlanningOption(panelOption)"
            />
          </div>
        </div>
      </el-form-item>
    </div>
    <div class="production-planning-capacity-list">
      <el-form-item :label="$t('planning.netCurtainsRoomHigh')">
        <el-table ref="planningList" :data="netCurtainRoomHighItems" border fit highlight-current-row>
          <el-table-column :label="$t('planning.workshopOrder')" width="50px" property="workshopOrder">
            <template slot-scope="scope">
              <span>{{ scope.row.workshopOrder }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.workshopID')" width="250px" property="workshopID">
            <template slot-scope="{ row }">
              <el-select
                v-model="row.workshopID"
                :disabled="currentPlanning.netCurtainsRoomHighPreselectedYN || netCurtainsRoomHighPlannedYN"
                size="mini"
                type="select"
                clearable
                @input="handleInputWorkshop(row, $event)"
              >
                <el-option-group :label="$t('planning.workshopID')">
                  <el-option
                    v-for="workshop in getSelectableWorkshops(row.workshopID, netCurtainRoomHighItems)"
                    :key="workshop.id"
                    :label="workshop.name"
                    :value="workshop.id"
                  />
                </el-option-group>
              </el-select>
              <div v-if="row.workshopEmpty" class="validation-errors">{{ $t('planning.workshopRequired') }}</div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.capacity')" width="200px" property="capacity">
            <template slot-scope="{ row }">
              <MDinput
                v-model="row.capacity"
                type="number"
                :readonly="readonly"
                :precision="0"
                :disabled="currentPlanning.netCurtainsRoomHighPreselectedYN || netCurtainsRoomHighPlannedYN"
                @input="handleInputCapacity(row, $event)"
              />
              <div v-if="row.capacityEmpty" class="validation-errors">{{ $t('planning.capacityRequired') }}</div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.estimatedDeliveryDate')" width="200px" property="estimatedDeliveryDate">
            <template slot-scope="{ row }">
              <date-picker v-model="row.estimatedDeliveryDate" type="date" />
            </template>
          </el-table-column>
          <el-table-column>
            <template slot-scope="{ row }">
              <span v-if="row.ignoreExcludedFabricsYN" style="color:red">
                {{ $t('planning.excludedFabricsWillBeIgnored') }}
              </span>
            </template>
          </el-table-column>
        </el-table>
        <div
          v-if="currentPlanning && currentPlanning.netCurtainsRoomHighPreselectedYN && !netCurtainsRoomHighPlannedYN"
          class="validation-errors"
        >
          {{ $t('planning.preselectionMade') }}
        </div>
        <div v-if="netCurtainsRoomHighPlannedYN" class="validation-errors">
          {{ $t('planning.alreadyPlanned') }}
          <br />
          <editor-button
            :icon-only="false"
            :disabled="!selectedDomainID || selectedWeek != currentWeek"
            :button-string="$t('planning.planAgain')"
            action="secondary"
            style="margin-top:5px;"
            @click="planAgain(netCurtainRoomHighOption)"
          />
        </div>
        <div v-show="!netCurtainsRoomHighPlannedYN">
          <div style="padding-top:5px;">
            <el-checkbox
              v-model="netCurtainsRoomHighConsiderStoreAttributes"
              :disabled="
                !currentPlanning ||
                  currentPlanning.netCurtainsRoomHighPreselectedYN ||
                  netCurtainsRoomHighPlannedYN ||
                  selectedWeek != currentWeek
              "
              style="padding-left: 20px"
            >
              {{ $t('planning.considerStoreAttributes') }}
            </el-checkbox>
            <el-checkbox
              v-model="samplesOnlyYN[netCurtainRoomHighOption]"
              :disabled="
                !currentPlanning ||
                  currentPlanning.netCurtainsRoomHighPreselectedYN ||
                  netCurtainsRoomHighPlannedYN ||
                  selectedWeek != currentWeek
              "
              style="padding-left: 20px"
            >
              {{ $t('planning.samples') }}
            </el-checkbox>
            <span style="padding-left:20px">
              <label class="material-label">
                {{ $t('planning.excludedStores') }}
              </label>
              <el-select
                v-model="excludedStoreIDs[netCurtainRoomHighOption]"
                :multiple="true"
                :placeholder="$t('planning.excludedStores')"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.netCurtainsRoomHighPreselectedYN ||
                    netCurtainsRoomHighPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%; margin-bottom: 10px"
              >
                <el-option
                  v-for="store in stores"
                  :key="store.id"
                  :label="store.code + ', ' + store.name"
                  :value="store.id"
                />
              </el-select>
            </span>
            <span style="margin-left:20px">
              <label class="material-label">
                {{ $t('planning.excludedStoreAttributes') }}
              </label>
              <el-select
                v-model="excludedStoreAttributeIDs[netCurtainRoomHighOption]"
                :multiple="true"
                :placeholder="$t('planning.excludedStoreAttributes')"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.netCurtainsRoomHighPreselectedYN ||
                    netCurtainsRoomHighPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%"
              >
                <el-option
                  v-for="storeAttribute in storeAttributes"
                  :key="storeAttribute.id"
                  :label="storeAttribute.name"
                  :value="storeAttribute.id"
                />
              </el-select>
            </span>
            <span style="padding-left:20px">
              <label class="material-label">
                {{ $t('planning.installationService') }}
              </label>
              <el-select
                v-model="installationServiceOptions[netCurtainRoomHighOption]"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.netCurtainsRoomHighPreselectedYN ||
                    netCurtainsRoomHighPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width:20%;margin-bottom:10px"
              >
                <el-option
                  v-for="option in planInstallationServiceOptions"
                  :key="option.code"
                  :label="option.description"
                  :value="option.code"
                />
              </el-select>
            </span>
          </div>
          <div>
            <editor-button
              :icon-only="false"
              :disabled="netCurtainsRoomHighPlannedYN || !selectedDomainID || selectedWeek != currentWeek"
              :button-string="$t('planning.plan')"
              action="primary"
              style="float:left"
              @click="startSelectionWizard(netCurtainRoomHighOption)"
            />
            <editor-button
              :icon-only="false"
              :disabled="netCurtainsRoomHighPlannedYN || !selectedDomainID || selectedWeek != currentWeek"
              button-translation="planning.reset"
              action="secondary"
              style="margin-top:0px;float:left"
              class="reset-button"
              @click="resetPlanningOption(netCurtainRoomHighOption)"
            />
          </div>
        </div>
      </el-form-item>
    </div>
    <div class="production-planning-capacity-list">
      <el-form-item :label="$t('planning.curtainsRoomHigh')">
        <el-table ref="planningList" :data="curtainRoomHighItems" border fit highlight-current-row>
          <el-table-column :label="$t('planning.workshopOrder')" width="50px" property="workshopOrder">
            <template slot-scope="scope">
              <span>{{ scope.row.workshopOrder }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.workshopID')" width="250px" property="workshopID">
            <template slot-scope="{ row }">
              <el-select
                v-model="row.workshopID"
                :disabled="currentPlanning.curtainsRoomHighPreselectedYN || curtainsRoomHighPlannedYN"
                size="mini"
                type="select"
                clearable
                @input="handleInputWorkshop(row, $event)"
              >
                <el-option-group :label="$t('planning.workshopID')">
                  <el-option
                    v-for="workshop in getSelectableWorkshops(row.workshopID, curtainRoomHighItems)"
                    :key="workshop.id"
                    :label="workshop.name"
                    :value="workshop.id"
                  />
                </el-option-group>
              </el-select>
              <div v-if="row.workshopEmpty" class="validation-errors">{{ $t('planning.workshopRequired') }}</div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.capacity')" width="200px" property="capacity">
            <template slot-scope="{ row }">
              <MDinput
                v-model="row.capacity"
                type="number"
                :readonly="readonly"
                :precision="0"
                :disabled="currentPlanning.curtainsRoomHighPreselectedYN || curtainsRoomHighPlannedYN"
                @input="handleInputCapacity(row, $event)"
              />
              <div v-if="row.capacityEmpty" class="validation-errors">{{ $t('planning.capacityRequired') }}</div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.estimatedDeliveryDate')" width="200px" property="estimatedDeliveryDate">
            <template slot-scope="{ row }">
              <date-picker v-model="row.estimatedDeliveryDate" type="date" />
            </template>
          </el-table-column>
          <el-table-column>
            <template slot-scope="{ row }">
              <span v-if="row.ignoreExcludedFabricsYN" style="color:red">
                {{ $t('planning.excludedFabricsWillBeIgnored') }}
              </span>
            </template>
          </el-table-column>
        </el-table>
        <div
          v-if="currentPlanning && currentPlanning.curtainsRoomHighPreselectedYN && !curtainsRoomHighPlannedYN"
          class="validation-errors"
        >
          {{ $t('planning.preselectionMade') }}
        </div>
        <div v-if="curtainsRoomHighPlannedYN" class="validation-errors">
          {{ $t('planning.alreadyPlanned') }}
          <br />
          <editor-button
            :icon-only="false"
            :disabled="!selectedDomainID || selectedWeek != currentWeek"
            :button-string="$t('planning.planAgain')"
            action="secondary"
            style="margin-top:5px;"
            @click="planAgain(curtainRoomHighOption)"
          />
        </div>
        <div v-show="!curtainsRoomHighPlannedYN">
          <div style="padding-top:5px">
            <el-checkbox
              v-model="curtainsRoomHighConsiderStoreAttributes"
              :disabled="
                !currentPlanning ||
                  currentPlanning.curtainsRoomHighPreselectedYN ||
                  curtainsRoomHighPlannedYN ||
                  selectedWeek != currentWeek
              "
              style="padding-left: 20px"
            >
              {{ $t('planning.considerStoreAttributes') }}
            </el-checkbox>
            <el-checkbox
              v-model="samplesOnlyYN[curtainRoomHighOption]"
              :disabled="
                !currentPlanning ||
                  currentPlanning.curtainsRoomHighPreselectedYN ||
                  curtainsRoomHighPlannedYN ||
                  selectedWeek != currentWeek
              "
              style="padding-left: 20px"
            >
              {{ $t('planning.samples') }}
            </el-checkbox>
            <span style="padding-left:20px">
              <label class="material-label">
                {{ $t('planning.excludedStores') }}
              </label>
              <el-select
                v-model="excludedStoreIDs[curtainRoomHighOption]"
                :multiple="true"
                :placeholder="$t('planning.excludedStores')"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.curtainsRoomHighPreselectedYN ||
                    curtainsRoomHighPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%; margin-bottom: 10px"
              >
                <el-option
                  v-for="store in stores"
                  :key="store.id"
                  :label="store.code + ', ' + store.name"
                  :value="store.id"
                />
              </el-select>
            </span>
            <span style="margin-left:20px">
              <label class="material-label">
                {{ $t('planning.excludedStoreAttributes') }}
              </label>
              <el-select
                v-model="excludedStoreAttributeIDs[curtainRoomHighOption]"
                :multiple="true"
                :placeholder="$t('planning.excludedStoreAttributes')"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.curtainsRoomHighPreselectedYN ||
                    curtainsRoomHighPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%"
              >
                <el-option
                  v-for="storeAttribute in storeAttributes"
                  :key="storeAttribute.id"
                  :label="storeAttribute.name"
                  :value="storeAttribute.id"
                />
              </el-select>
            </span>
            <span style="padding-left:20px">
              <label class="material-label">
                {{ $t('planning.installationService') }}
              </label>
              <el-select
                v-model="installationServiceOptions[curtainRoomHighOption]"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.curtainsRoomHighPreselectedYN ||
                    curtainsRoomHighPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width:20%;padding-bottom:10px"
              >
                <el-option
                  v-for="option in planInstallationServiceOptions"
                  :key="option.code"
                  :label="option.description"
                  :value="option.code"
                />
              </el-select>
            </span>
          </div>
          <div>
            <editor-button
              :icon-only="false"
              :disabled="curtainsRoomHighPlannedYN || !selectedDomainID || selectedWeek != currentWeek"
              :button-string="$t('planning.plan')"
              action="primary"
              style="float:left"
              @click="startSelectionWizard(curtainRoomHighOption)"
            />
            <editor-button
              :icon-only="false"
              :disabled="curtainsRoomHighPlannedYN || !selectedDomainID || selectedWeek != currentWeek"
              button-translation="planning.reset"
              action="secondary"
              class="reset-button"
              style="float:left;margin-top:0px;"
              @click="resetPlanningOption(curtainRoomHighOption)"
            />
          </div>
        </div>
      </el-form-item>
    </div>
    <div class="production-planning-capacity-list">
      <el-form-item :label="$t('planning.romanBlinds')">
        <el-table ref="planningList" :data="romanBlindItems" border fit highlight-current-row>
          <el-table-column :label="$t('planning.workshopOrder')" width="50px" property="workshopOrder">
            <template slot-scope="scope">
              <span>{{ scope.row.workshopOrder }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.workshopID')" width="250px" property="workshopID">
            <template slot-scope="{ row }">
              <el-select
                v-model="row.workshopID"
                :disabled="currentPlanning.romanBlindsPreselectedYN || romanBlindsPlannedYN"
                size="mini"
                type="select"
                clearable
                @input="handleInputWorkshop(row, $event)"
              >
                <el-option-group :label="$t('planning.workshopID')">
                  <el-option
                    v-for="workshop in getSelectableWorkshops(row.workshopID, romanBlindItems)"
                    :key="workshop.id"
                    :label="workshop.name"
                    :value="workshop.id"
                  />
                </el-option-group>
              </el-select>
              <div v-if="row.workshopEmpty" class="validation-errors">{{ $t('planning.workshopRequired') }}</div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.capacity')" width="200px" property="capacity">
            <template slot-scope="{ row }">
              <MDinput
                v-model="row.capacity"
                type="number"
                :readonly="readonly"
                :precision="0"
                :disabled="currentPlanning.romanBlindsPreselectedYN || romanBlindsPlannedYN"
                @input="handleInputCapacity(row, $event)"
              />
              <div v-if="row.capacityEmpty" class="validation-errors">{{ $t('planning.capacityRequired') }}</div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.estimatedDeliveryDate')" width="200px" property="estimatedDeliveryDate">
            <template slot-scope="{ row }">
              <date-picker v-model="row.estimatedDeliveryDate" type="date" />
            </template>
          </el-table-column>
          <el-table-column>
            <template slot-scope="{ row }">
              <span v-if="row.ignoreExcludedFabricsYN" style="color:red">
                {{ $t('planning.excludedFabricsWillBeIgnored') }}
              </span>
            </template>
          </el-table-column>
        </el-table>
        <div
          v-if="currentPlanning && currentPlanning.romanBlindsPreselectedYN && !romanBlindsPlannedYN"
          class="validation-errors"
        >
          {{ $t('planning.preselectionMade') }}
        </div>
        <div v-if="romanBlindsPlannedYN" class="validation-errors">
          {{ $t('planning.alreadyPlanned') }}
          <br />
          <editor-button
            :icon-only="false"
            :disabled="!selectedDomainID || selectedWeek != currentWeek"
            :button-string="$t('planning.planAgain')"
            action="secondary"
            style="margin-top:5px;"
            @click="planAgain(romanBlindOption)"
          />
        </div>
        <div v-show="!romanBlindsPlannedYN">
          <div style="padding-top:5px">
            <el-checkbox
              v-model="romanBlindsConsiderStoreAttributes"
              :disabled="
                !currentPlanning ||
                  currentPlanning.romanBlindsPreselectedYN ||
                  romanBlindsPlannedYN ||
                  selectedWeek != currentWeek
              "
              style="padding-left: 20px"
            >
              {{ $t('planning.considerStoreAttributes') }}
            </el-checkbox>
            <el-checkbox
              v-model="samplesOnlyYN[romanBlindOption]"
              :disabled="
                !currentPlanning ||
                  currentPlanning.romanBlindsPreselectedYN ||
                  romanBlindsPlannedYN ||
                  selectedWeek != currentWeek
              "
              style="padding-left: 20px"
            >
              {{ $t('planning.samples') }}
            </el-checkbox>
            <span style="padding-left:20px">
              <label class="material-label">
                {{ $t('planning.excludedStores') }}
              </label>
              <el-select
                v-model="excludedStoreIDs[romanBlindOption]"
                :multiple="true"
                :placeholder="$t('planning.excludedStores')"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.romanBlindsPreselectedYN ||
                    romanBlindsPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%; margin-bottom: 10px"
              >
                <el-option
                  v-for="store in stores"
                  :key="store.id"
                  :label="store.code + ', ' + store.name"
                  :value="store.id"
                />
              </el-select>
            </span>
            <span style="margin-left:20px">
              <label class="material-label">
                {{ $t('planning.excludedStoreAttributes') }}
              </label>
              <el-select
                v-model="excludedStoreAttributeIDs[romanBlindOption]"
                :multiple="true"
                :placeholder="$t('planning.excludedStoreAttributes')"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.romanBlindsPreselectedYN ||
                    romanBlindsPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%"
              >
                <el-option
                  v-for="storeAttribute in storeAttributes"
                  :key="storeAttribute.id"
                  :label="storeAttribute.name"
                  :value="storeAttribute.id"
                />
              </el-select>
            </span>
            <span style="padding-left:20px">
              <label class="material-label">
                {{ $t('planning.installationService') }}
              </label>
              <el-select
                v-model="installationServiceOptions[romanBlindOption]"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.romanBlindsPreselectedYN ||
                    romanBlindsPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width:20%;padding-bottom:10px"
              >
                <el-option
                  v-for="option in planInstallationServiceOptions"
                  :key="option.code"
                  :label="option.description"
                  :value="option.code"
                />
              </el-select>
            </span>
          </div>
          <div>
            <editor-button
              :icon-only="false"
              :disabled="romanBlindsPlannedYN || !selectedDomainID || selectedWeek != currentWeek"
              :button-string="$t('planning.plan')"
              action="primary"
              style="float:left"
              @click="startSelectionWizard(romanBlindOption)"
            />
            <editor-button
              :icon-only="false"
              :disabled="romanBlindsPlannedYN || !selectedDomainID || selectedWeek != currentWeek"
              button-translation="planning.reset"
              action="secondary"
              class="reset-button"
              style="float:left;margin-top:0px"
              @click="resetPlanningOption(romanBlindOption)"
            />
          </div>
        </div>
      </el-form-item>
    </div>
    <div class="production-planning-capacity-list">
      <el-form-item :label="$t('planning.cuttings')">
        <el-table ref="planningList" :data="cuttingItems" border fit highlight-current-row>
          <el-table-column :label="$t('planning.workshopOrder')" width="50px" property="workshopOrder">
            <template slot-scope="scope">
              <span>{{ scope.row.workshopOrder }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.workshopID')" width="250px" property="workshopID">
            <template slot-scope="{ row }">
              <el-select
                v-model="row.workshopID"
                :disabled="currentPlanning.cuttingsPreselectedYN || cuttingsPlannedYN"
                size="mini"
                type="select"
                clearable
                @input="handleInputWorkshop(row, $event)"
              >
                <el-option-group :label="$t('planning.workshopID')">
                  <el-option
                    v-for="workshop in getSelectableWorkshops(row.workshopID, cuttingItems)"
                    :key="workshop.id"
                    :label="workshop.name"
                    :value="workshop.id"
                  />
                </el-option-group>
              </el-select>
              <div v-if="row.workshopEmpty" class="validation-errors">{{ $t('planning.workshopRequired') }}</div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.capacity')" width="200px" property="capacity">
            <template slot-scope="{ row }">
              <MDinput
                v-model="row.capacity"
                type="number"
                :readonly="readonly"
                :precision="0"
                :disabled="currentPlanning.cuttingsPreselectedYN || cuttingsPlannedYN"
                @input="handleInputCapacity(row, $event)"
              />
              <div v-if="row.capacityEmpty" class="validation-errors">{{ $t('planning.capacityRequired') }}</div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('planning.estimatedDeliveryDate')" width="200px" property="estimatedDeliveryDate">
            <template slot-scope="{ row }">
              <date-picker v-model="row.estimatedDeliveryDate" type="date" />
            </template>
          </el-table-column>
          <el-table-column>
            <template slot-scope="{ row }">
              <span v-if="row.ignoreExcludedFabricsYN" style="color:red">
                {{ $t('planning.excludedFabricsWillBeIgnored') }}
              </span>
            </template>
          </el-table-column>
        </el-table>
        <div
          v-if="currentPlanning && currentPlanning.cuttingsPreselectedYN && !cuttingsPlannedYN"
          class="validation-errors"
        >
          {{ $t('planning.preselectionMade') }}
        </div>
        <div v-if="cuttingsPlannedYN" class="validation-errors">
          {{ $t('planning.alreadyPlanned') }}
          <br />
          <editor-button
            :icon-only="false"
            :disabled="!selectedDomainID || selectedWeek != currentWeek"
            :button-string="$t('planning.planAgain')"
            action="secondary"
            style="margin-top:5px;"
            @click="planAgain(cuttingOption)"
          />
        </div>
        <div v-show="!cuttingsPlannedYN">
          <div style="padding-top:5px">
            <el-checkbox
              v-model="cuttingsConsiderStoreAttributes"
              :disabled="
                !currentPlanning ||
                  currentPlanning.cuttingsPreselectedYN ||
                  cuttingsPlannedYN ||
                  selectedWeek != currentWeek
              "
              style="padding-left: 20px"
            >
              {{ $t('planning.considerStoreAttributes') }}
            </el-checkbox>
            <el-checkbox
              v-model="samplesOnlyYN[cuttingOption]"
              :disabled="
                !currentPlanning ||
                  currentPlanning.cuttingsPreselectedYN ||
                  cuttingsPlannedYN ||
                  selectedWeek != currentWeek
              "
              style="padding-left: 20px"
            >
              {{ $t('planning.samples') }}
            </el-checkbox>
            <span style="padding-left:20px">
              <label class="material-label">
                {{ $t('planning.excludedStores') }}
              </label>
              <el-select
                v-model="excludedStoreIDs[cuttingOption]"
                :multiple="true"
                :placeholder="$t('planning.excludedStores')"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.cuttingsPreselectedYN ||
                    cuttingsPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%; margin-bottom: 10px"
              >
                <el-option
                  v-for="store in stores"
                  :key="store.id"
                  :label="store.code + ', ' + store.name"
                  :value="store.id"
                />
              </el-select>
            </span>
            <span style="margin-left:20px">
              <label class="material-label">
                {{ $t('planning.excludedStoreAttributes') }}
              </label>
              <el-select
                v-model="excludedStoreAttributeIDs[cuttingOption]"
                :multiple="true"
                :placeholder="$t('planning.excludedStoreAttributes')"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.cuttingsPreselectedYN ||
                    cuttingsPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width: 20%"
              >
                <el-option
                  v-for="storeAttribute in storeAttributes"
                  :key="storeAttribute.id"
                  :label="storeAttribute.name"
                  :value="storeAttribute.id"
                />
              </el-select>
            </span>
            <span style="padding-left:20px">
              <label class="material-label">
                {{ $t('planning.installationService') }}
              </label>
              <el-select
                v-model="installationServiceOptions[cuttingOption]"
                class="search-select"
                :disabled="
                  !currentPlanning ||
                    currentPlanning.cuttingsPreselectedYN ||
                    cuttingsPlannedYN ||
                    selectedWeek != currentWeek
                "
                type="select"
                style="width:20%;padding-bottom:10px"
              >
                <el-option
                  v-for="option in planInstallationServiceOptions"
                  :key="option.code"
                  :label="option.description"
                  :value="option.code"
                />
              </el-select>
            </span>
          </div>
          <div>
            <editor-button
              :icon-only="false"
              :disabled="cuttingsPlannedYN || !selectedDomainID || selectedWeek != currentWeek"
              :button-string="$t('planning.plan')"
              action="primary"
              style="float:left"
              @click="startSelectionWizard(cuttingOption)"
            />
            <editor-button
              :icon-only="false"
              :disabled="cuttingsPlannedYN || !selectedDomainID || selectedWeek != currentWeek"
              button-translation="planning.reset"
              action="secondary"
              class="reset-button"
              style="float:left;margin-top:0px"
              @click="resetPlanningOption(cuttingOption)"
            />
          </div>
        </div>
      </el-form-item>
    </div>
    <el-dialog
      :title="$t('planning.ordersToPlan')"
      :visible.sync="dialogVisible"
      :close-on-click-modal="false"
      :append-to-body="true"
      :width="'1500px'"
    >
      <orders-to-plan-dialogue :domain-i-d="selectedDomainID" />
      &nbsp;
    </el-dialog>
  </div>
</template>

<script>
import EditorButton from '@/components/crud/EditorButton';
import { PlanningOption } from '@/utils/constants';
import MDinput from '@/components/MDinput';
import baseCrudMixin from '@/mixins/crud';
import i18n from '@/i18n';
import OrdersToPlanDialogue from './OrdersToPlanDialogue';
import { ForceFileDownload } from '@/utils/general';
import DatePicker from '@/components/DSE/DatePicker';

export default {
  components: {
    EditorButton,
    OrdersToPlanDialogue,
    MDinput,
    DatePicker
  },
  mixins: [baseCrudMixin],
  props: {
    value: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    initialValue: Array,
    caption: String,
    itemsProp: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      selectedDomainID: this.$store.getters['domain/getDefaultSelectValueDomainID'],
      dialogVisible: false,

      selectedWeek: null,
      currentWeek: null,
      availableWeeks: [],
      currentPlanning: null,
      currentPlanningInProgress: false,

      panelItems: [],
      panelsPlannedYN: false,
      panelOption: PlanningOption.Panel,
      panelsConsiderStoreAttributes: false,

      netCurtainRoomHighItems: [],
      netCurtainsRoomHighPlannedYN: false,
      netCurtainRoomHighOption: PlanningOption.NetCurtainRoomHigh,
      netCurtainsRoomHighConsiderStoreAttributes: false,

      curtainRoomHighItems: [],
      curtainsRoomHighPlannedYN: false,
      curtainRoomHighOption: PlanningOption.CurtainRoomHigh,
      curtainsRoomHighConsiderStoreAttributes: false,

      romanBlindItems: [],
      romanBlindsPlannedYN: false,
      romanBlindOption: PlanningOption.RomanBlind,
      romanBlindsConsiderStoreAttributes: false,

      cuttingItems: [],
      cuttingsPlannedYN: false,
      cuttingOption: PlanningOption.Cutting,
      cuttingsConsiderStoreAttributes: false,

      excludedStoreIDs: {},
      excludedStoreAttributeIDs: {},
      installationServiceOptions: {
        0: 0, // set planningoption enumeration values to 0 (no special treatment for installationservice orders)
        1: 0,
        2: 0,
        3: 0,
        4: 0
      },
      samplesOnlyYN: {
        0: false,
        1: false,
        2: false,
        3: false,
        4: false
      }
    };
  },
  computed: {
    domains() {
      return this.$store.getters.domains;
    },
    stores() {
      return this.$store.getters['store/loadedItems'];
    },
    storeAttributes() {
      return this.$store.getters['storeAttribute/loadedItems'];
    },
    planInstallationServiceOptions() {
      return this.$store.getters['option/loadedOptions']('planningInstallationServiceOptions');
    }
  },
  watch: {
    value(value) {
      this.items = value;
    }
  },
  created() {
    if (this.selectedDomainID) {
      this.handleDomainChange(this.selectedDomainID);
    }
  },
  methods: {
    getSelectableWorkshops(selectedWorkshop, items) {
      return this.allWorkshops.filter(
        workshop =>
          selectedWorkshop === workshop.id ||
          (!workshop.planningDisabledYN && !items.some(e => e.workshopID === workshop.id))
      );
    },
    handleInputWorkshop(row, workshopID) {
      const workshop = this.allWorkshops.find(workshop => workshop.id === workshopID);
      row.ignoreExcludedFabricsYN = workshop ? workshop.ignoreExcludedFabricsYN : false;
    },
    handleInputCapacity(row, capacity) {
      row.capacity = capacity;
    },
    async fetchCapacityPlanningForDomain() {
      await this.$store.dispatch('workshop/getItems');
      await this.$store.dispatch('store/clearItems');
      await this.$store.dispatch('store/getItems', { query: { domainid: this.selectedDomainID, sort: 'code' } });
      await this.$store.dispatch('storeAttribute/clearItems');
      await this.$store.dispatch('storeAttribute/getItems', { query: { domainID: this.selectedDomainID } });
      if (!this.allWorkshops) {
        this.allWorkshops = await this.$store.getters['workshop/loadedItems'];
      }

      let currentPlanning;
      if (this.selectedWeek) {
        currentPlanning = await this.$store.dispatch('productionPlanning/getByWeek', {
          domainID: this.selectedDomainID,
          week: this.selectedWeek
        });
      } else {
        currentPlanning = await this.$store.dispatch('productionPlanning/getCurrent', {
          domainID: this.selectedDomainID
        });
      }

      this.currentPlanning = currentPlanning;
      const panelCapacity = currentPlanning.productionPlanningWorkshops.filter(
        pc => pc.planningOption === PlanningOption.Panel
      );
      const netCurtainRoomHighCapacity = currentPlanning.productionPlanningWorkshops.filter(
        pc => pc.planningOption === PlanningOption.NetCurtainRoomHigh
      );
      const curtainRoomHighCapacity = currentPlanning.productionPlanningWorkshops.filter(
        pc => pc.planningOption === PlanningOption.CurtainRoomHigh
      );
      const romanBlindCapacity = currentPlanning.productionPlanningWorkshops.filter(
        pc => pc.planningOption === PlanningOption.RomanBlind
      );
      const cuttingCapacity = currentPlanning.productionPlanningWorkshops.filter(
        pc => pc.planningOption === PlanningOption.Cutting
      );
      const workshops = this.$store.getters['workshop/loadedItems'];
      this.addEmptyCapacityRows(panelCapacity, PlanningOption.Panel, workshops.length);
      this.addEmptyCapacityRows(netCurtainRoomHighCapacity, PlanningOption.NetCurtainRoomHigh, workshops.length);
      this.addEmptyCapacityRows(curtainRoomHighCapacity, PlanningOption.CurtainRoomHigh, workshops.length);
      this.addEmptyCapacityRows(romanBlindCapacity, PlanningOption.RomanBlind, workshops.length);
      this.addEmptyCapacityRows(cuttingCapacity, PlanningOption.Cutting, workshops.length);
      this.setWorkshopOrderNumbers(panelCapacity);
      this.setWorkshopOrderNumbers(netCurtainRoomHighCapacity);
      this.setWorkshopOrderNumbers(curtainRoomHighCapacity);
      this.setWorkshopOrderNumbers(romanBlindCapacity);
      this.setWorkshopOrderNumbers(cuttingCapacity);

      panelCapacity.forEach(i => this.handleInputWorkshop(i, i.workshopID));
      this.panelItems = panelCapacity;
      this.panelsPlannedYN = currentPlanning.panelsPlannedYN;
      this.panelsConsiderStoreAttributes = panelCapacity[0].considerStoreAttributesYN || false;
      netCurtainRoomHighCapacity.forEach(i => this.handleInputWorkshop(i, i.workshopID));
      this.netCurtainRoomHighItems = netCurtainRoomHighCapacity;
      this.netCurtainsRoomHighPlannedYN = currentPlanning.netCurtainsRoomHighPlannedYN;
      this.netCurtainsRoomHighConsiderStoreAttributes =
        netCurtainRoomHighCapacity[0].considerStoreAttributesYN || false;
      curtainRoomHighCapacity.forEach(i => this.handleInputWorkshop(i, i.workshopID));
      this.curtainRoomHighItems = curtainRoomHighCapacity;
      this.curtainsRoomHighPlannedYN = currentPlanning.curtainsRoomHighPlannedYN;
      this.curtainsRoomHighConsiderStoreAttributes = curtainRoomHighCapacity[0].considerStoreAttributesYN || false;
      romanBlindCapacity.forEach(i => this.handleInputWorkshop(i, i.workshopID));
      this.romanBlindItems = romanBlindCapacity;
      this.romanBlindsPlannedYN = currentPlanning.romanBlindsPlannedYN;
      this.romanBlindsConsiderStoreAttributes = romanBlindCapacity[0].considerStoreAttributesYN || false;
      cuttingCapacity.forEach(i => this.handleInputWorkshop(i, i.workshopID));
      this.cuttingItems = cuttingCapacity;
      this.cuttingsPlannedYN = currentPlanning.cuttingsPlannedYN;
      this.cuttingsConsiderStoreAttributes = cuttingCapacity[0].considerStoreAttributesYN || false;
      if (this.selectedWeek == null) {
        this.currentWeek = currentPlanning.weekByDate;
        this.currentPlanningInProgress =
          this.currentPlanning.panelsPreselectedYN ||
          this.currentPlanning.curtainsRoomHighPreselectedYN ||
          this.currentPlanning.netCurtainsRoomHighPreselectedYN ||
          this.currentPlanning.romanBlindsPreselectedYN ||
          this.currentPlanning.cuttingsPreselectedYN;
      }
      this.selectedWeek = currentPlanning.weekByDate;
      this.$forceUpdate();
    },
    exportSelection() {
      this.loading = true;
      this.$store
        .dispatch('productionPlanning/exportSelection', {
          domainID: this.selectedDomainID,
          weekByDate: this.selectedWeek
        })
        .then(blob => {
          ForceFileDownload(blob, `OrderDump_week${this.selectedWeek}.xlsx`);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getOrdersToPlan() {
      this.dialogVisible = true;
    },
    makeCurrent() {
      if (this.currentPlanningInProgress) {
        this.$store.dispatch('notify/error', {
          message: this.$i18n.t('planning.planningInProgress') + this.currentWeek
        });
        return;
      }
      this.$store
        .dispatch('productionPlanning/makeCurrent', {
          domainID: this.selectedDomainID,
          weekByDate: this.selectedWeek
        })
        .then(() => {
          this.$store.dispatch('notify/success', { message: this.$i18n.t('common.data_updated') });
          this.currentWeek = this.selectedWeek;
        });
    },
    async saveData() {
      this.panelItems.forEach(i => {
        i.considerStoreAttributesYN = this.panelsConsiderStoreAttributes;
      });
      this.netCurtainRoomHighItems.forEach(i => {
        i.considerStoreAttributesYN = this.netCurtainsRoomHighConsiderStoreAttributes;
      });
      this.curtainRoomHighItems.forEach(i => {
        i.considerStoreAttributesYN = this.netCurtainsRoomHighConsiderStoreAttributes;
      });
      this.romanBlindItems.forEach(i => {
        i.considerStoreAttributesYN = this.romanBlindsConsiderStoreAttributes;
      });
      this.cuttingItems.forEach(i => {
        i.considerStoreAttributesYN = this.cuttingsConsiderStoreAttributes;
      });

      const allCapacities = this.panelItems
        .concat(
          this.netCurtainRoomHighItems.concat(
            this.curtainRoomHighItems.concat(this.romanBlindItems.concat(this.cuttingItems))
          )
        )
        .filter(i => i.workshopID && i.capacity && i.capacity > 0);

      const request = {
        domainID: this.selectedDomainID,
        weekByDate: this.selectedWeek,
        productionPlanningWorkshops: allCapacities
      };
      await this.$store.dispatch('productionPlanning/updateProductionPlanning', request).then(() => {
        this.$store.dispatch('notify/success', { message: i18n.t('common.data_updated') });
      });
    },
    addEmptyCapacityRows(productCapacity, planningOption, numberOfWorkshops) {
      while (productCapacity.length < numberOfWorkshops) {
        productCapacity.push({
          workshopID: null,
          planningOption: planningOption,
          capacity: null
        });
      }
    },
    setWorkshopOrderNumbers(productCapacity) {
      let workshopOrder = 1;
      productCapacity.forEach(e => (e.workshopOrder = workshopOrder++));
    },
    async handleDomainChange(domainID) {
      this.loading = true;
      this.selectedDomainID = domainID;
      this.selectedWeek = null;
      await this.fetchCapacityPlanningForDomain().finally(() => {
        this.loading = false;
      });
    },
    handleWeekChange(weeknumber) {
      this.loading = true;
      this.fetchCapacityPlanningForDomain().finally(() => {
        this.loading = false;
      });
    },
    planAgain(planningOption) {
      this.loading = true;
      const domainID = this.selectedDomainID;
      const week = this.selectedWeek;
      this.$store
        .dispatch('productionPlanning/planAgain', { planningOption, domainID, week })
        .then(() => {
          this.fetchCapacityPlanningForDomain();
        })
        .catch(err => {
          this.$store.dispatch('notify/error', err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async startSelectionWizard(planningOption) {
      if (!this.selectedWeek) {
        return;
      }
      const year = this.selectedWeek.substring(0, 4);
      if (year !== new Date().getFullYear().toString()) {
        try {
          await this.$store.dispatch('notify/confirm', {
            message: `${this.$i18n.t('planning.confirmYear')} ${year}?`
          });
        } catch {
          return;
        }
      }

      try {
        await this.saveData();
        const domainID = this.selectedDomainID;
        this.$emit('action', 'startSelectionStep', {
          domainID: domainID,
          planningOption: planningOption,
          excludedStoreIDs: this.excludedStoreIDs[planningOption],
          excludedStoreAttributeIDs: this.excludedStoreAttributeIDs[planningOption],
          installationServiceOption: this.installationServiceOptions[planningOption],
          samplesOnlyYN: this.samplesOnlyYN[planningOption]
        });
      } catch (err) {
        await this.$store.dispatch('notify/error', err);
      }
    },
    async resetPlanningOption(planningOption) {
      this.loading = true;
      const domainID = this.selectedDomainID;
      try {
        await this.$store.dispatch('productionPlanning/resetPlanningOption', { planningOption, domainID });
        await this.fetchCapacityPlanningForDomain();
        this.currentPlanningInProgress =
          this.currentPlanning.panelsPreselectedYN ||
          this.currentPlanning.curtainsRoomHighPreselectedYN ||
          this.currentPlanning.netCurtainsRoomHighPreselectedYN ||
          this.currentPlanning.romanBlindsPreselectedYN ||
          this.currentPlanning.cuttingsPreselectedYN;
        this.$store.dispatch('notify/success', { message: this.$i18n.t('common.data_updated') });
      } catch (err) {
        this.$store.dispatch('notify/error', err);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
<style lang="scss">
.production-planning-capacity-list {
  margin-top: 10px;
}

.reset-button {
  background-color: #ff8c8c;
  color: #fff;
  margin-left: 0px;
  margin-top: 5px;
  float: right;
  &:disabled {
    background-color: #ffc0bf;
    color: #fff;
    pointer-events: none;
  }
}
</style>
